import { t, type I18nLocale } from '@ardoq/i18n';
import { GroupType } from '@ardoq/api-types';
import {
  ItemsType,
  type ErrorInfoBoxLabels as PackageErrorInfoBoxLabels,
  type GetNodeLimitMessageArgs,
  type GetRuleErrorPrefixArgs,
} from '@ardoq/error-info-box';

export type ErrorInfoBoxLabels = Required<PackageErrorInfoBoxLabels>;

const getGroupTypeNames = (
  i18nLocale: I18nLocale
): Record<GroupType, string> => ({
  [GroupType.CHILD]: t(
    i18nLocale,
    'errorInfoBox:groupType.child',
    'Group by child'
  ),
  [GroupType.COMPONENT]: t(
    i18nLocale,
    'errorInfoBox:groupType.component',
    'Group by component type'
  ),
  [GroupType.FIELD]: t(
    i18nLocale,
    'errorInfoBox:groupType.field',
    'Group by field value'
  ),
  [GroupType.PARENT]: t(
    i18nLocale,
    'errorInfoBox:groupType.parent',
    'Group by parent'
  ),
  [GroupType.PARENT_ALL]: t(
    i18nLocale,
    'errorInfoBox:groupType.parentAll',
    'Group by parent all'
  ),
  [GroupType.REFERENCE]: t(
    i18nLocale,
    'errorInfoBox:groupType.reference',
    'Group by reference type'
  ),
  [GroupType.REFERENCE_CONTEXTUAL]: t(
    i18nLocale,
    'errorInfoBox:groupType.referenceContextual',
    'Group by reference contextual type'
  ),
  [GroupType.REFERENCE_TYPE_NAME]: t(
    i18nLocale,
    'errorInfoBox:groupType.referenceTypeName',
    'Group by reference type name'
  ),
  [GroupType.TAG]: t(i18nLocale, 'errorInfoBox:groupType.tag', 'Group by tag'),
  [GroupType.WORKSPACE]: t(
    i18nLocale,
    'errorInfoBox:groupType.workspace',
    'Group by workspace'
  ),
  [GroupType.ALL]: t(i18nLocale, 'errorInfoBox:groupType.all', 'Group all'),
});

const buildRuleErrorPrefix =
  (i18nLocale: I18nLocale) =>
  ({ ordinal, ruleTypeName }: GetRuleErrorPrefixArgs): string =>
    t(
      i18nLocale,
      'errorInfoBox:ruleErrorPrefix',
      { ordinal, ruleTypeName },
      'The {{ordinal}} rule, {{ruleTypeName}}, has the following problem:'
    );

export const getErrorInfoBoxLabels = (
  i18nLocale: I18nLocale
): ErrorInfoBoxLabels => ({
  detailsLabel: t(i18nLocale, 'errorInfoBox:detailsLabel', 'Details'),
  closeButtonLabel: t(i18nLocale, 'errorInfoBox:closeButtonLabel', 'Close'),
  cloneInfoTitle: t(
    i18nLocale,
    'errorInfoBox:cloneInfoTitle',
    'Some of the grouping rules have created duplicate components.'
  ),
  cloneInfoDescription: t(
    i18nLocale,
    'errorInfoBox:cloneInfoDescription',
    'This can be confusing at first, but it is normal for some grouping rules to result in duplicated components. Grouping by reference type or by parents are common grouping scenarios which often produce duplicates.'
  ),
  neverShowAgainLabel: t(
    i18nLocale,
    'errorInfoBox:neverShowAgainLabel',
    'Never show this message again'
  ),
  rulesNotAppliedLabel: t(
    i18nLocale,
    'errorInfoBox:rulesNotAppliedLabel',
    'These rules could not be applied:'
  ),
  groupTypeNames: getGroupTypeNames(i18nLocale),
  getRuleErrorPrefix: buildRuleErrorPrefix(i18nLocale),
});

export type NodeLimitErrorLabels = {
  getMessage: (args: GetNodeLimitMessageArgs) => string;
  detailsText: string;
  buttonLabel: string;
};

const getItemsTypeLabel = (
  i18nLocale: I18nLocale,
  itemsType: ItemsType
): string => {
  switch (itemsType) {
    case ItemsType.COMPONENTS:
      return t(
        i18nLocale,
        'errorInfoBox:nodeLimit.itemsType.components',
        'components'
      );
    case ItemsType.REFERENCES:
      return t(
        i18nLocale,
        'errorInfoBox:nodeLimit.itemsType.references',
        'references'
      );
    case ItemsType.COMPONENTS_AND_REFERENCES:
      return t(
        i18nLocale,
        'errorInfoBox:nodeLimit.itemsType.componentsAndReferences',
        'components and references'
      );
  }
};

const buildNodeLimitMessage =
  (i18nLocale: I18nLocale) =>
  ({
    itemsType,
    formattedItemCount,
    formattedLimit,
  }: GetNodeLimitMessageArgs): string =>
    t(
      i18nLocale,
      'errorInfoBox:nodeLimit.message',
      {
        itemsType: getItemsTypeLabel(i18nLocale, itemsType),
        formattedItemCount,
        formattedLimit,
      },
      'There are too many {{itemsType}} to show ({{formattedItemCount}}). We have displayed the first {{formattedLimit}}. Larger datasets may cause the view to run slowly.'
    );

export const getNodeLimitErrorLabels = (
  i18nLocale: I18nLocale
): NodeLimitErrorLabels => ({
  getMessage: buildNodeLimitMessage(i18nLocale),
  detailsText: t(
    i18nLocale,
    'errorInfoBox:nodeLimit.detailsText',
    'Narrow down your selection by choosing a component from the navigator on the left, applying filters, or adjusting the view settings.'
  ),
  buttonLabel: t(
    i18nLocale,
    'errorInfoBox:nodeLimit.buttonLabel',
    'Proceed anyway'
  ),
});
